import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { getMyBranches, setWorkingBranch, Branch } from "../../services/api";
import { useAuth, isBranchPromptPending, clearBranchPrompt } from "../../context/AuthContext";
import { isStaff } from "../../utils/roles";
import { FaMapMarkerAlt } from "react-icons/fa";

/**
 * Asks a staff member which branch they are working at, once per sign-in.
 *
 * Whatever they pick is stamped onto every booking they make, so it is what the
 * branch sales figures are built from. Staff rotate between branches, which is
 * why this is asked each time rather than fixed to their profile.
 *
 * Shown as a blocking overlay because a booking made before the branch is set
 * would be attributed to wherever they worked last — silently wrong, and only
 * discovered when the numbers are read weeks later.
 */
const EXEMPT_PATHS = new Set(["/login", "/signup"]);

const BranchPrompt: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user, profile, loading } = useAuth();
    const location = useLocation();

    const [pending, setPending] = useState(isBranchPromptPending());
    const [branches, setBranches] = useState<Branch[]>([]);
    const [selected, setSelected] = useState("");
    const [loaded, setLoaded] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const needed = pending && !loading && !!user && isStaff(profile) && !EXEMPT_PATHS.has(location.pathname);

    // Re-read the flag on navigation: ensureSession may set it after this
    // component first mounted.
    useEffect(() => { setPending(isBranchPromptPending()); }, [location.pathname, user, profile]);

    const load = useCallback(async () => {
        try {
            // Only the branches this person is assigned to. Offering one they
            // cannot use would fail on submit with no way forward.
            const { branches: list, current } = await getMyBranches();
            setBranches(list);
            // The main branch is the default when this person has it, because
            // that is where most people are most days and it makes the common
            // case a single click.
            //
            // Anyone not assigned to the main branch falls back to where they
            // worked last, and then to the first branch they hold — never to a
            // branch they are not assigned to, which the server would refuse on
            // save and leave them stuck on this screen.
            setSelected(
                list.find((b) => b.isDefault)?.officeId
                    ?? (list.some((b) => b.officeId === current) ? current! : undefined)
                    ?? list[0]?.officeId
                    ?? "",
            );
        } catch {
            setError("Could not load your branches. Please refresh.");
        } finally {
            setLoaded(true);
        }
    }, [profile?.officeId]);

    useEffect(() => { if (needed) load(); }, [needed, load]);

    if (!needed) return <>{children}</>;

    const confirm = async () => {
        if (!selected) return;
        setSaving(true);
        setError("");
        try {
            await setWorkingBranch(selected);
            clearBranchPrompt();
            setPending(false);
        } catch (e: any) {
            setError(e?.response?.data?.errors?.[0]?.message
                ?? e?.response?.data?.error
                ?? "That branch could not be set.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            {children}
            <div
                className="fixed inset-0 z-[2147483646] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
                role="dialog"
                aria-modal="true"
            >
                <div className="w-full max-w-sm rounded-2xl bg-white p-7 shadow-2xl">
                    <div className="mb-4 flex justify-center">
                        <FaMapMarkerAlt className="text-primary" size={32} />
                    </div>

                    <h2 className="mb-2 text-center text-lg font-semibold text-gray-900">
                        Which branch are you at today?
                    </h2>
                    <p className="mb-5 text-center text-sm text-gray-500">
                        Every booking you make will be recorded against this branch.
                    </p>

                    {loaded && branches.length === 0 && !error && (
                        <p className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                            You have not been assigned to a branch yet. Please contact head office.
                        </p>
                    )}

                    <select
                        value={selected}
                        onChange={(e) => setSelected(e.target.value)}
                        disabled={saving || branches.length === 0}
                        className="mb-4 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
                    >
                        {!loaded && <option value="">Loading…</option>}
                        {branches.map((b) => (
                            <option key={b.officeId} value={b.officeId}>{b.name}</option>
                        ))}
                    </select>

                    {error && <div className="mb-3 text-sm text-red-600">{error}</div>}

                    <button
                        onClick={confirm}
                        disabled={saving || !selected}
                        className="w-full rounded-lg bg-primary py-2.5 text-sm font-semibold text-white transition hover:bg-blue-800 disabled:opacity-50"
                    >
                        {saving ? "Saving…" : "Start work"}
                    </button>
                </div>
            </div>
        </>
    );
};

export default BranchPrompt;
